import { useLazyQuery } from '@apollo/client'
import {
  Card,
  CardContent,
  Divider,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import { isEmpty } from 'lodash'
import React, { useEffect } from 'react'

import { useRefresh } from '@/contexts/RefreshContext'
import {
  ActionType,
  GET_LEAST_COMPLETED_ACTIONS,
  GetLeastCompletedActionsQuery,
  GetLeastCompletedActionsQueryVariables,
} from '@/graphql'
import useT from '@/i18n/useT'
import { getRAGColor, mockThresholds } from '@/mocks/mockStoresReportTable'
import { DateHelpers } from '@/utils/date.helpers'

type Props = {
  title: string
  startTime?: string
  endTime?: string
  storeIds?: string[]
  actionTypes?: ActionType[]
  isRestock?: boolean
  actionShiftIds?: string[]
  showTime?: boolean
  showCompletion?: boolean
}

export const TaskNeverDoneStat: React.FC<Props> = props => {
  const { t } = useT()
  const {
    title,
    startTime,
    endTime,
    storeIds,
    actionTypes,
    actionShiftIds,
    isRestock,
    showTime = false,
    showCompletion = false,
  } = props
  const [
    getLeastComplete,
    { data: leastCompleteData, loading, refetch, variables },
  ] = useLazyQuery<
    GetLeastCompletedActionsQuery,
    GetLeastCompletedActionsQueryVariables
  >(GET_LEAST_COMPLETED_ACTIONS, { notifyOnNetworkStatusChange: true })

  useRefresh(() => {
    if (!isEmpty(variables)) {
      refetch()
    }
  })

  const leastCompleteActions = leastCompleteData?.leastCompletedActions || []
  const leastComplete = leastCompleteActions.slice(0, 5) || []

  useEffect(() => {
    if (startTime && endTime) {
      getLeastComplete({
        fetchPolicy: 'network-only',
        variables: {
          filters: {
            startTime,
            endTime,
            storeIds,
            actionTypes,
            isRestock,
            actionShiftIds,
          },
        },
      })
    }
  }, [
    startTime,
    endTime,
    isRestock,
    JSON.stringify(storeIds),
    JSON.stringify(actionTypes),
    JSON.stringify(actionShiftIds),
  ])

  return (
    <Card sx={{ flex: 1 }}>
      <CardContent>
        {loading ? (
          <>
            <Skeleton />
            <Skeleton />
            <Skeleton />
          </>
        ) : (
          <>
            <Typography variant="h6" mb={4}>
              {title}
            </Typography>
            <Stack spacing={1}>
              {leastComplete.length > 0 ? (
                leastComplete.map(task => (
                  <React.Fragment key={task.id}>
                    <Stack direction={'row'} spacing={4} alignItems={'center'}>
                      <Tooltip title={task.title} arrow placement="top">
                        <Typography
                          variant="body2"
                          sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            flex: 1,
                          }}
                        >
                          {task.title}
                        </Typography>
                      </Tooltip>
                      {showTime && task.dueDateTime && (
                        <Typography>
                          {DateHelpers.formatTime(task.dueDateTime)}
                        </Typography>
                      )}
                      {showCompletion &&
                        task.completionPercentage !== undefined && (
                          <Typography
                            color={getRAGColor(
                              mockThresholds.taskCompletion,
                              task.completionPercentage / 100,
                            )}
                          >
                            {Math.round(task.completionPercentage)}%
                          </Typography>
                        )}
                    </Stack>
                    <Divider />
                  </React.Fragment>
                ))
              ) : (
                <Typography variant="body2">
                  {t('tasks.noTasksFound')}
                </Typography>
              )}
            </Stack>
          </>
        )}
      </CardContent>
    </Card>
  )
}
